import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, InputBase, Chip, IconButton, Fab, Button,
  AppBar, Toolbar, Tooltip, Divider,
  CircularProgress, Alert, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Rating,
  TextField, Menu, MenuItem, ListItemIcon, Snackbar,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/ViewList';
import LayersIcon from '@mui/icons-material/Layers';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ClearIcon from '@mui/icons-material/Clear';
import DirectionsIcon from '@mui/icons-material/Directions';
import SortIcon from '@mui/icons-material/Sort';
import CasinoIcon from '@mui/icons-material/Casino';
import CheckIcon from '@mui/icons-material/Check';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import TuneIcon from '@mui/icons-material/Tune';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribePlaces, addPlace, updatePlace, deletePlace,
  subscribeProgramPlaces,
} from '../../utils/firebase';
import { getCurrentPosition, placeDistanceKm } from '../../utils/geo';
import { takePendingShare, fetchInstagramUnfurl } from '../../utils/shareTarget';
import { pinIcon, circleIcon, userLocationIcon, haloIcon, statusColor, STATUS_COLORS } from '../../utils/markers';
import PlaceCard, { isPhotoExpired } from './PlaceCard';
import PlaceFormDialog from './PlaceFormDialog';
import PlaceDetailSheet from './PlaceDetailSheet';
import { recordCall, canRefreshPhoto } from '../../utils/apiUsage';
import ApiUsagePanel, { ApiUsageChip } from '../common/ApiUsagePanel';
import AppSwitcher from '../common/AppSwitcher';
import SharedListsDrawer from '../lists/SharedListsDrawer';
import { googleMapsUrl } from '../lists/SharedListPage';
import { PROGRAM_META, ALL_PROGRAMS } from '../lists/programMeta';
import Avatar from '@mui/material/Avatar';

const SG_CENTER = { lat: 1.3521, lng: 103.8198 };
const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9fa8da' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0d0d14' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#16213e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#0d0d14' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0d1b2a' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2e1a' }] },
];
// Google Maps native controls fight the bottom controls (map/list toggle,
// legend, FAB). On mobile the zoom buttons go top-right and compass up top
// so the whole bottom is ours.
function makeMapOptions(isMobile) {
  const g = window.google;
  const base = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    styles: MAP_STYLES,
    // Keep gestures smooth so taps are responsive.
    gestureHandling: 'greedy',
    clickableIcons: false,
  };
  if (!g) return base;
  return {
    ...base,
    zoomControl: !isMobile, // native pinch-zoom is fine on mobile; buttons steal space
    zoomControlOptions: { position: g.maps.ControlPosition.RIGHT_TOP },
  };
}

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recently added' },
  { value: 'name', label: 'Name A–Z' },
  { value: 'rating', label: 'Highest rated' },
  { value: 'distance', label: 'Nearest first' },
];

const CATEGORY_FILTERS = [
  { value: 'mustgo', label: 'Must Go', color: STATUS_COLORS.mustgo },
  { value: 'luxury', label: 'Luxury', color: STATUS_COLORS.luxury },
  { value: 'healthy', label: 'Healthy', color: STATUS_COLORS.healthy },
];

const LEGEND_STATUS = [
  { key: 'mustgo', label: 'Must Go' },
  { key: 'luxury', label: 'Luxury' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'default', label: 'Want to try' },
  { key: 'visited', label: 'Visited' },
];

const statusKey = (p) =>
  p.visited ? 'visited' : (STATUS_COLORS[p.category] ? p.category : 'default');

function sortPlaces(list, sortBy, userLoc) {
  if (sortBy === 'name') return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (sortBy === 'rating') return [...list].sort((a, b) => (b.visitedRating ?? -1) - (a.visitedRating ?? -1));
  if (sortBy === 'distance' && userLoc) {
    return [...list].sort((a, b) =>
      (placeDistanceKm(userLoc, a) ?? Infinity) - (placeDistanceKm(userLoc, b) ?? Infinity));
  }
  return list; // 'recent' — subscription already orders by createdAt desc
}

function getMarkerIcon(place, selected, mobile) {
  if (place._source === 'program') {
    const prog = (place.programs || []).find(p => PROGRAM_META[p]);
    return circleIcon(PROGRAM_META[prog]?.marker || '#90a4ae', { selected, mobile });
  }
  return pinIcon(statusColor(place), { selected, mobile });
}

// Visited dialog
function VisitedDialog({ place, open, onClose, onSave }) {
  const [rating, setRating] = useState(null);
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) { setRating(null); setNotes(''); }
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { bgcolor: '#16162a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 } }}>
      <DialogTitle fontWeight={700}>Mark as Visited</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">How was <strong>{place?.name}</strong>?</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rating:</Typography>
          <Rating value={rating} onChange={(_, v) => setRating(v)} sx={{ '& .MuiRating-iconFilled': { color: '#ff7043' } }} />
        </Box>
        <TextField label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)}
          multiline rows={2} fullWidth size="small" placeholder="What did you eat? How was it?" />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
        <Button onClick={() => onSave(rating, notes)} variant="contained" size="small">Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PlacesPage({ mapsLoaded }) {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [places, setPlaces] = useState([]);
  const [programPlaces, setProgramPlaces] = useState([]);
  const [activeLayers, setActiveLayers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCuisine, setFilterCuisine] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTab, setFilterTab] = useState('all');
  const [mobileView, setMobileView] = useState('list');
  const [sortBy, setSortBy] = useState('recent');
  const [sortAnchor, setSortAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState(null); // { message, undoPlace? }
  const [userLoc, setUserLoc] = useState(null);
  const [locating, setLocating] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [sharePrefill, setSharePrefill] = useState(null);
  const [visitedDialogPlace, setVisitedDialogPlace] = useState(null);
  const [detailSheetPlace, setDetailSheetPlace] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [listsDrawerOpen, setListsDrawerOpen] = useState(false);
  const [usagePanelOpen, setUsagePanelOpen] = useState(false);

  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [infoWindowPlace, setInfoWindowPlace] = useState(null);

  const mapRef = useRef(null);
  const listRef = useRef(null);
  const boundsFittedRef = useRef(false);
  const pendingFocusRef = useRef(null);
  const photoRefreshRef = useRef(new Set());

  // Subscribe to personal places
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const unsub = subscribePlaces(user.uid, (data) => {
      setPlaces(data);
      setLoading(false);
    }, (err) => {
      setError(`${err.code}: ${err.message}`);
      setLoading(false);
    });
    return unsub;
  }, [user]);

  // Subscribe to program places (public collection)
  useEffect(() => {
    const unsub = subscribeProgramPlaces(setProgramPlaces);
    return unsub;
  }, []);

  // Pick up a pending "Share to FoodList" from Instagram (see
  // utils/shareTarget.js) and open the Add Place dialog pre-filled. We also
  // fire a best-effort unfurl in the background — Instagram actively blocks
  // crawlers now, so it usually returns nothing. If it does land, it fills
  // the name/caption; if not, the form just stays as-is for manual entry.
  useEffect(() => {
    if (!user) return;
    const pending = takePendingShare();
    if (!pending) return;
    setSharePrefill({ instagram: pending.instagramUrl || '', remarks: pending.remainder || '' });
    setEditingPlace(null);
    setFormOpen(true);

    let cancelled = false;
    fetchInstagramUnfurl(pending.instagramUrl).then(unfurl => {
      if (cancelled || !unfurl) return;
      if (!unfurl.guessedName && !unfurl.caption && !unfurl.imageUrl) return;
      setSharePrefill(prev => ({
        ...(prev || {}),
        instagram: pending.instagramUrl || '',
        remarks: pending.remainder || '',
        name: unfurl.guessedName || '',
        instagramCaption: unfurl.caption || '',
        photoURL: unfurl.imageUrl || '',
      }));
    });
    return () => { cancelled = true; };
  }, [user]);

  // Google Places photo URLs go stale (~72h). Refresh a few per snapshot;
  // the resulting Firestore update re-triggers this effect so the backlog
  // drains gradually. Two hard caps keep this from running up a bill:
  //   - photoRefreshRef skips places already attempted this session
  //   - canRefreshPhoto() enforces a per-day limit in localStorage. See
  //     utils/apiUsage.js for the ceiling.
  useEffect(() => {
    if (!mapsLoaded || loading || !window.google?.maps?.places) return;
    if (!canRefreshPhoto()) return;
    const stale = places
      .filter(p => p.placeId && isPhotoExpired(p) && !photoRefreshRef.current.has(p.id))
      .slice(0, 2);
    if (!stale.length) return;
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    stale.forEach(p => {
      if (!canRefreshPhoto()) return;
      photoRefreshRef.current.add(p.id);
      recordCall('photoRefresh');
      service.getDetails({ placeId: p.placeId, fields: ['photos'] }, (result, status) => {
        if (status === 'OK' && result?.photos?.length) {
          updatePlace(p.id, {
            photoURL: result.photos[0].getUrl({ maxWidth: 400 }),
            photoGeneratedAt: Date.now(),
          }).catch(() => {});
        }
      });
    });
  }, [mapsLoaded, loading, places]);

  const locate = async () => {
    setLocating(true);
    try {
      const loc = await getCurrentPosition();
      setUserLoc(loc);
      return loc;
    } catch (e) {
      setSnackbar({ message: e.message });
      return null;
    } finally {
      setLocating(false);
    }
  };

  const handleNearMe = async () => {
    const loc = await locate();
    if (loc && mapRef.current) {
      mapRef.current.panTo(loc);
      mapRef.current.setZoom(15);
    }
  };

  const handleSortSelect = async (value) => {
    setSortAnchor(null);
    if (value === 'distance' && !userLoc) {
      const loc = await locate();
      if (!loc) return;
    }
    setSortBy(value);
  };

  const toggleLayer = (program) => {
    setActiveLayers(prev => {
      const next = new Set(prev);
      if (next.has(program)) next.delete(program); else next.add(program);
      return next;
    });
  };

  // Visible program places based on active layers
  const visibleProgramPlaces = programPlaces.filter(p =>
    (p.programs || []).some(prog => activeLayers.has(prog))
  ).map(p => ({ ...p, _source: 'program' }));

  // Search across name, cuisine, address and notes
  const matchesSearch = (p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [p.name, p.cuisine, p.address, p.remarks, p.notes]
      .some(f => f && f.toLowerCase().includes(q));
  };

  // Filter personal places
  const filteredPersonal = places.filter(p => {
    if (filterTab === 'want' && p.visited) return false;
    if (filterTab === 'visited' && !p.visited) return false;
    if (filterCuisine && p.cuisine !== filterCuisine) return false;
    if (filterCategory && p.category !== filterCategory) return false;
    return matchesSearch(p);
  });

  // Filter program places
  const filteredProgram = visibleProgramPlaces.filter(p => {
    if (filterCuisine && p.cuisine !== filterCuisine) return false;
    return matchesSearch(p);
  });

  const sortedPersonal = sortPlaces(filteredPersonal, sortBy, userLoc);

  // Visited always goes under a separate "Visited" section at the bottom.
  // Within each group we keep whatever sort the user picked.
  const unvisitedPersonal = sortedPersonal.filter(p => !p.visited);
  const visitedPersonal = sortedPersonal.filter(p => p.visited);

  const displayedList = [
    ...unvisitedPersonal.map(p => ({ ...p, _source: 'personal' })),
    ...visitedPersonal.map(p => ({ ...p, _source: 'personal' })),
    ...filteredProgram,
  ];

  // Markers mirror the list: searching or filtering narrows the map too
  const mapPlaces = displayedList.filter(p => p.latitude && p.longitude);

  // Fit the map around the user's places once, when both the map and the
  // first snapshot of places are ready. The GoogleMap `center` prop stays a
  // constant so re-renders (typing in search, toggling filters) don't yank
  // the viewport back.
  const fitInitialBounds = () => {
    if (boundsFittedRef.current || !mapRef.current || loading) return;
    const pts = places.filter(p => p.latitude && p.longitude);
    if (pts.length === 0) return;
    boundsFittedRef.current = true;
    if (pts.length === 1) {
      mapRef.current.setCenter({ lat: pts[0].latitude, lng: pts[0].longitude });
      mapRef.current.setZoom(14);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      pts.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
      mapRef.current.fitBounds(bounds, 48);
    }
  };
  useEffect(fitInitialBounds);

  // Pan/zoom to a place. If the map isn't mounted yet (mobile list view),
  // remember it and apply once the map loads. On mobile we also offset the
  // pan upward so the pin stays visible above the bottom detail sheet.
  const focusOnMap = (place, { offsetForSheet = false } = {}) => {
    if (!place.latitude || !place.longitude) return;
    if (mapRef.current) {
      mapRef.current.panTo({ lat: place.latitude, lng: place.longitude });
      mapRef.current.setZoom(16);
      if (offsetForSheet && isMobile) {
        // After the pan/zoom settles, slide the viewport up so the pin
        // appears in the upper third (above the sheet). ~34% of viewport.
        setTimeout(() => {
          try {
            const h = window.innerHeight || 720;
            mapRef.current && mapRef.current.panBy(0, -Math.round(h * 0.22));
          } catch (_) {}
        }, 60);
      }
    } else {
      pendingFocusRef.current = place;
    }
  };

  const handleSurprise = () => {
    // Prefer unvisited places from the current filters; fall back to anything visible
    const unvisited = sortedPersonal.filter(p => !p.visited && p.latitude && p.longitude);
    const pool = unvisited.length ? unvisited : mapPlaces;
    if (!pool.length) { setSnackbar({ message: 'No places to pick from — add some first!' }); return; }
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const pickWithSrc = { ...pick, _source: pick._source || 'personal' };
    setSelectedMarkerId(pick.id);
    setSnackbar({ message: `🎲 Tonight: ${pick.name}` });
    if (isMobile) {
      setMobileView('map');
      setDetailSheetPlace(pickWithSrc);
      focusOnMap(pick, { offsetForSheet: true });
    } else {
      setInfoWindowPlace(pick);
      focusOnMap(pick);
    }
    const el = listRef.current?.querySelector(`[data-place-id="${pick.id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const stats = {
    total: places.length,
    visited: places.filter(p => p.visited).length,
    want: places.filter(p => !p.visited).length,
  };

  const handleSave = async (data) => {
    if (editingPlace) {
      await updatePlace(editingPlace.id, data);
    } else {
      await addPlace(user.uid, { ...data, visited: false, visitedRating: null, visitedNotes: '' });
    }
    setEditingPlace(null);
  };

  // Delete immediately with an Undo window instead of a confirm() prompt
  const handleDelete = async (place) => {
    await deletePlace(place.id);
    if (infoWindowPlace?.id === place.id) setInfoWindowPlace(null);
    setSnackbar({ message: `Deleted ${place.name}`, undoPlace: place });
  };

  const handleUndoDelete = async () => {
    const p = snackbar?.undoPlace;
    setSnackbar(null);
    if (!p) return;
    const { id, userId, createdAt, updatedAt, ...data } = p;
    await addPlace(user.uid, data);
    setSnackbar({ message: `Restored ${p.name}` });
  };

  const handleToggleVisited = (place) => {
    if (place.visited) {
      updatePlace(place.id, { visited: false, visitedRating: null, visitedNotes: '' });
    } else {
      setVisitedDialogPlace(place);
    }
  };

  const handleVisitedSave = async (rating, notes) => {
    await updatePlace(visitedDialogPlace.id, { visited: true, visitedRating: rating, visitedNotes: notes });
    setVisitedDialogPlace(null);
  };

  const handleAddProgramPlaceToList = async (place) => {
    await addPlace(user.uid, {
      name: place.name,
      address: place.address || '',
      latitude: place.latitude || null,
      longitude: place.longitude || null,
      cuisine: place.cuisine || '',
      category: '',
      instagram: '',
      remarks: place.notes || '',
      visited: false,
      visitedRating: null,
      visitedNotes: '',
      photoURL: place.photoURL || null,
      photoGeneratedAt: null,
      placeId: place.placeId || null,
      programs: place.programs || [],
    });
  };

  // Opening a place from the list / map.
  //  - Mobile: everything opens the bottom-sheet detail (big tap targets,
  //    scrollable, keeps the map visible above it).
  //  - Desktop personal: keep the compact InfoWindow pattern on the map.
  //  - Desktop program: use the sheet too, because InfoWindow lacks the
  //    "add to my list" affordance program places need.
  const handlePlaceClick = (place) => {
    setSelectedMarkerId(place.id);
    const useSheet = isMobile || place._source === 'program';
    if (useSheet) {
      setInfoWindowPlace(null);
      setDetailSheetPlace(place);
      if (place.latitude && place.longitude) {
        if (isMobile && mobileView === 'map') focusOnMap(place, { offsetForSheet: true });
        else focusOnMap(place);
      }
      return;
    }
    if (place.latitude && place.longitude) {
      setInfoWindowPlace(place);
      focusOnMap(place);
    }
  };

  const handleMarkerClick = (place) => {
    setSelectedMarkerId(place.id);
    const useSheet = isMobile || place._source === 'program';
    if (useSheet) {
      setInfoWindowPlace(null);
      setDetailSheetPlace(place);
      if (isMobile) focusOnMap(place, { offsetForSheet: true });
      return;
    }
    setInfoWindowPlace(place);
    const el = listRef.current?.querySelector(`[data-place-id="${place.id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleDetailEdit = (place) => {
    setDetailSheetPlace(null);
    setEditingPlace(place);
    setFormOpen(true);
  };
  const handleDetailDelete = (place) => {
    setDetailSheetPlace(null);
    handleDelete(place);
  };
  const handleDetailToggleVisited = (place) => {
    // Keep the sheet open when flipping back to "not visited"; close it
    // before the rating dialog opens so modals don't stack.
    if (place.visited) {
      updatePlace(place.id, { visited: false, visitedRating: null, visitedNotes: '' });
      setDetailSheetPlace({ ...place, visited: false, visitedRating: null, visitedNotes: '' });
    } else {
      setDetailSheetPlace(null);
      setVisitedDialogPlace(place);
    }
  };

  // Cuisines sorted by how many places use them
  const cuisineCounts = places.reduce((acc, p) => {
    if (p.cuisine) acc[p.cuisine] = (acc[p.cuisine] || 0) + 1;
    return acc;
  }, {});
  const cuisinesInUse = Object.keys(cuisineCounts).sort((a, b) => cuisineCounts[b] - cuisineCounts[a]);
  const categoriesInUse = CATEGORY_FILTERS.filter(c => places.some(p => p.category === c.value));

  // Legend reflects only the marker types currently on the map
  const personalStatusesOnMap = new Set(
    mapPlaces.filter(p => p._source === 'personal').map(statusKey)
  );
  const legendItems = [
    ...LEGEND_STATUS.filter(s => personalStatusesOnMap.has(s.key))
      .map(s => ({ label: s.label, color: STATUS_COLORS[s.key], shape: 'pin' })),
    ...ALL_PROGRAMS.filter(prog => activeLayers.has(prog))
      .map(prog => ({ label: PROGRAM_META[prog].short, color: PROGRAM_META[prog].marker, shape: 'circle' })),
  ];

  // Horizontally scrolling pill row (food-app filter bar pattern)
  const scrollRowSx = {
    px: 2, py: 0.75, display: 'flex', gap: 0.6, alignItems: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    overflowX: 'auto', flexWrap: 'nowrap',
    scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
    '& > *': { flexShrink: 0 },
  };

  const layerBar = (
    <Box sx={scrollRowSx}>
      <LayersIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.25 }} />
      {ALL_PROGRAMS.map(prog => {
        const meta = PROGRAM_META[prog];
        const active = activeLayers.has(prog);
        const count = programPlaces.filter(p => (p.programs || []).includes(prog)).length;
        return (
          <Chip
            key={prog}
            label={`${meta.short}${count ? ` ${count}` : ''}`}
            size="small"
            onClick={() => toggleLayer(prog)}
            variant={active ? 'filled' : 'outlined'}
            sx={{
              cursor: 'pointer', fontSize: '0.72rem',
              height: isMobile ? 30 : 24,
              borderRadius: 99,
              bgcolor: active ? meta.bg : 'transparent',
              color: active ? meta.marker : 'text.disabled',
              borderColor: active ? meta.marker : 'rgba(255,255,255,0.1)',
              fontWeight: active ? 700 : 500,
            }}
          />
        );
      })}
    </Box>
  );

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      {/* Masthead: wordmark in Fraunces italic, two actions. No border. */}
      <Box sx={{
        display: 'flex', alignItems: 'baseline', gap: 1,
        px: 2.25, pt: 2.25, pb: 1.5,
      }}>
        <AppSwitcher>
          <Typography sx={{
            fontFamily: '"Fraunces", serif',
            fontStyle: 'italic', fontWeight: 600,
            fontSize: '1.4rem', letterSpacing: '-0.02em',
            color: 'text.primary',
            lineHeight: 1,
          }}>
            foodlist
          </Typography>
        </AppSwitcher>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setListsDrawerOpen(true)}
          sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'text.primary' } }}>
          <ListAltIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={logout}
          sx={{ color: 'text.disabled', p: 0.5, '&:hover': { color: 'text.secondary' } }}>
          <LogoutIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Tab row: text-underline, no chunky pills. Count as a soft superscript. */}
      <Box sx={{ display: 'flex', px: 2.25, gap: 2.5, borderBottom: '1px solid rgba(244,239,233,0.06)' }}>
        {[
          { label: 'All', value: stats.total, tab: 'all' },
          { label: 'To try', value: stats.want, tab: 'want' },
          { label: 'Visited', value: stats.visited, tab: 'visited' },
        ].map(({ label, value, tab }) => {
          const active = filterTab === tab;
          return (
            <Box
              key={tab}
              onClick={() => setFilterTab(tab)}
              sx={{
                position: 'relative',
                py: 1, cursor: 'pointer', userSelect: 'none',
                display: 'flex', alignItems: 'baseline', gap: 0.5,
                transition: 'color .16s',
                color: active ? 'text.primary' : 'text.disabled',
                '&:hover': { color: active ? 'text.primary' : 'text.secondary' },
                '&::after': active ? {
                  content: '""', position: 'absolute',
                  left: 0, right: 0, bottom: -1, height: 2,
                  bgcolor: 'primary.main', borderRadius: 1,
                } : {},
              }}
            >
              <Box component="span" sx={{
                fontSize: '0.86rem', fontWeight: active ? 600 : 500,
                letterSpacing: '-0.005em',
              }}>
                {label}
              </Box>
              <Box component="span" sx={{
                fontSize: '0.66rem', color: 'text.disabled',
                fontWeight: 500, fontVariantNumeric: 'tabular-nums',
              }}>
                {value}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Layer toggles — only if there's actually program data */}
      {(activeLayers.size > 0 || programPlaces.length > 0) && layerBar}

      {/* Search: thin underline, no boxy pill */}
      <Box sx={{ px: 2.25, pt: 1.5, pb: 1 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: '1px solid rgba(244,239,233,0.08)',
          transition: 'border-color .18s',
          '&:focus-within': { borderColor: 'rgba(227,105,80,0.5)' },
          pb: 0.4,
        }}>
          <SearchIcon sx={{ color: 'text.disabled', fontSize: 17 }} />
          <InputBase
            placeholder="Search places, cuisines, areas"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              flex: 1,
              fontSize: '0.86rem', letterSpacing: '-0.003em',
              '& input::placeholder': { color: 'text.disabled', opacity: 1 },
            }}
          />
          {search && (
            <IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'text.disabled', p: 0.25 }}>
              <ClearIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
          <IconButton size="small" onClick={e => setSortAnchor(e.currentTarget)}
            sx={{ color: sortBy !== 'recent' ? 'primary.main' : 'text.disabled', p: 0.25 }}>
            <SortIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={handleSurprise}
            sx={{ color: 'text.disabled', p: 0.25 }}>
            <CasinoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Menu anchorEl={sortAnchor} open={!!sortAnchor} onClose={() => setSortAnchor(null)}>
          {SORT_OPTIONS.map(opt => (
            <MenuItem key={opt.value} dense selected={sortBy === opt.value}
              onClick={() => handleSortSelect(opt.value)}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {sortBy === opt.value && <CheckIcon sx={{ fontSize: 15, color: 'primary.main' }} />}
              </ListItemIcon>
              <Typography sx={{ fontSize: '0.84rem' }}>{opt.label}</Typography>
              {opt.value === 'distance' && (
                <MyLocationIcon sx={{ fontSize: 12, ml: 1, color: 'text.disabled' }} />
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Filter row — flat text tokens, not chips. Only renders when the user
          has any categories/cuisines in the list AND actively filtering. The
          "clear" interaction is just tapping the active one again. */}
      {(cuisinesInUse.length > 0 || categoriesInUse.length > 0) && (
        <Box sx={{
          px: 2.25, py: 0.9, display: 'flex', gap: 1.6, alignItems: 'center',
          overflowX: 'auto', flexWrap: 'nowrap',
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          '& > *': { flexShrink: 0 },
        }}>
          {categoriesInUse.map(({ value, label, color }) => {
            const active = filterCategory === value;
            return (
              <Box
                key={value}
                onClick={() => setFilterCategory(active ? '' : value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer',
                  fontSize: '0.72rem', fontWeight: active ? 600 : 500,
                  letterSpacing: '-0.003em',
                  color: active ? 'text.primary' : 'text.disabled',
                  transition: 'color .14s',
                  '&:hover': { color: 'text.secondary' },
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                {label}
              </Box>
            );
          })}
          {cuisinesInUse.map(c => {
            const active = filterCuisine === c;
            return (
              <Box
                key={c}
                onClick={() => setFilterCuisine(active ? '' : c)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '0.72rem', fontWeight: active ? 600 : 500,
                  letterSpacing: '-0.003em',
                  color: active ? 'primary.main' : 'text.disabled',
                  borderBottom: active ? '1px solid' : '1px solid transparent',
                  borderColor: active ? 'primary.main' : 'transparent',
                  transition: 'color .14s, border-color .14s',
                  '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                }}
              >
                {c} <Box component="span" sx={{ opacity: 0.6 }}>{cuisineCounts[c]}</Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Place list */}
      <Box ref={listRef} sx={{
        flex: 1, overflowY: 'auto',
        pt: 0.5, pb: 1.5,
        display: 'flex', flexDirection: 'column',
        // Hairline divider between cards, inset past the photo column
        '& > [data-place-id]:not(:last-of-type)::after, & > div > [data-place-id]:not(:last-of-type)::after': {
          content: '""', display: 'block', ml: 11, mr: 2,
          borderBottom: '1px solid rgba(244,239,233,0.055)',
        },
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={28} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mx: 2 }}>{error}</Alert>
        ) : displayedList.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6, mx: 3, py: 4, px: 2 }}>
            <Typography sx={{
              fontFamily: '"Fraunces", serif', fontStyle: 'italic',
              fontSize: '1.2rem', fontWeight: 500, mb: 0.75,
              letterSpacing: '-0.02em', color: 'text.primary',
            }}>
              {places.length === 0 ? 'Nothing saved yet' : 'No matches'}
            </Typography>
            <Typography sx={{
              fontSize: '0.84rem', color: 'text.secondary',
              lineHeight: 1.55,
            }}>
              {places.length === 0
                ? 'Share a reel from Instagram, or add a place to get started.'
                : 'Clear a filter to see more.'}
            </Typography>
            {places.length === 0 && (
              <Button variant="contained" startIcon={<AddIcon />}
                sx={{ mt: 2.5, px: 2.5 }} onClick={() => setFormOpen(true)}>
                Add a place
              </Button>
            )}
          </Box>
        ) : (
          <>
            {unvisitedPersonal.map(place => (
              <Box key={place.id} data-place-id={place.id}>
                <PlaceCard
                  place={place}
                  selected={selectedMarkerId === place.id}
                  distanceKm={placeDistanceKm(userLoc, place)}
                  onClick={() => handlePlaceClick({ ...place, _source: 'personal' })}
                  onEdit={p => { setEditingPlace(p); setFormOpen(true); }}
                  onDelete={handleDelete}
                  onToggleVisited={handleToggleVisited}
                />
              </Box>
            ))}

            {visitedPersonal.length > 0 && (
              <>
                {unvisitedPersonal.length > 0 && (
                  <Box sx={{
                    display: 'flex', alignItems: 'baseline', gap: 1,
                    mt: 2, mb: 0.5, px: 2.25,
                  }}>
                    <Typography sx={{
                      fontFamily: '"Fraunces", serif', fontStyle: 'italic',
                      fontSize: '0.9rem', fontWeight: 500,
                      color: 'text.secondary', letterSpacing: '-0.01em',
                    }}>
                      Visited
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.68rem', color: 'text.disabled',
                      fontWeight: 500, fontVariantNumeric: 'tabular-nums',
                    }}>
                      {visitedPersonal.length}
                    </Typography>
                    <Box sx={{ flex: 1, borderBottom: '1px solid rgba(244,239,233,0.055)', ml: 1, mb: 0.4 }} />
                  </Box>
                )}
                {visitedPersonal.map(place => (
                  <Box key={place.id} data-place-id={place.id}>
                    <PlaceCard
                      place={place}
                      selected={selectedMarkerId === place.id}
                      distanceKm={placeDistanceKm(userLoc, place)}
                      onClick={() => handlePlaceClick({ ...place, _source: 'personal' })}
                      onEdit={p => { setEditingPlace(p); setFormOpen(true); }}
                      onDelete={handleDelete}
                      onToggleVisited={handleToggleVisited}
                    />
                  </Box>
                ))}
              </>
            )}

            {filteredProgram.length > 0 && (
              <>
                {sortedPersonal.length > 0 && (
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }}>
                    <Typography variant="caption" color="text.disabled">Program Places</Typography>
                  </Divider>
                )}
                {filteredProgram.map(place => (
                  <ProgramPlaceCard
                    key={`prog-${place.id}`}
                    place={place}
                    selected={selectedMarkerId === place.id}
                    onClick={() => handlePlaceClick(place)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Box>

      {/* Footer: quiet API usage chip. Clicking opens the full breakdown. */}
      <Box sx={{
        px: 2, py: 1,
        display: 'flex', justifyContent: 'center',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}>
        <ApiUsageChip onClick={() => setUsagePanelOpen(true)} />
      </Box>

      {/* Add FAB — pill, smaller, calmer. Writ-text affordance, not just +. */}
      <Fab
        color="primary" variant="extended" size="medium"
        sx={{
          position: 'absolute',
          bottom: isMobile ? 'calc(78px + env(safe-area-inset-bottom))' : 20,
          right: 16, zIndex: 10,
          px: 2, py: 0.5, height: 40,
          fontSize: '0.84rem', fontWeight: 600,
          textTransform: 'none', letterSpacing: '-0.005em',
          boxShadow: '0 10px 24px rgba(227,105,80,0.35)',
          color: '#1a1216',
        }}
        onClick={() => { setEditingPlace(null); setFormOpen(true); }}
      >
        <AddIcon sx={{ mr: 0.5, fontSize: 18 }} /> Add
      </Fab>
    </Box>
  );

  const mapView = (
    <Box sx={{ flex: 1, position: 'relative', height: '100%' }}>
      {mapsLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={SG_CENTER}
          zoom={12}
          options={makeMapOptions(isMobile)}
          onLoad={map => {
            mapRef.current = map;
            // Refit on every map mount (initial load + mobile list/map toggle)
            boundsFittedRef.current = false;
            fitInitialBounds();
            // A tapped card / surprise pick wins over the bounds fit
            if (pendingFocusRef.current) {
              const p = pendingFocusRef.current;
              pendingFocusRef.current = null;
              map.setCenter({ lat: p.latitude, lng: p.longitude });
              map.setZoom(16);
            }
          }}
          onUnmount={() => { mapRef.current = null; }}
        >
          {/* Soft halo underneath the selected pin — makes the active tap
              target obvious even if adjacent markers overlap it. */}
          {selectedMarkerId && (() => {
            const sel = mapPlaces.find(p => p.id === selectedMarkerId);
            if (!sel) return null;
            return (
              <Marker
                position={{ lat: sel.latitude, lng: sel.longitude }}
                icon={haloIcon()}
                clickable={false}
                zIndex={999}
              />
            );
          })()}

          {mapPlaces.map(place => (
            <Marker
              key={`${place._source}-${place.id}`}
              position={{ lat: place.latitude, lng: place.longitude }}
              icon={getMarkerIcon(place, selectedMarkerId === place.id, isMobile)}
              onClick={() => handleMarkerClick(place)}
              zIndex={selectedMarkerId === place.id ? 1000 : undefined}
            />
          ))}

          {userLoc && (
            <Marker position={userLoc} icon={userLocationIcon()} clickable={false} zIndex={2000} />
          )}

          {!isMobile && infoWindowPlace && infoWindowPlace.latitude && (
            <InfoWindow
              position={{ lat: infoWindowPlace.latitude, lng: infoWindowPlace.longitude }}
              onCloseClick={() => setInfoWindowPlace(null)}
            >
              <Box sx={{ maxWidth: 220, fontFamily: '"Manrope", sans-serif' }}>
                {infoWindowPlace.photoURL && (
                  <img src={infoWindowPlace.photoURL} alt={infoWindowPlace.name}
                    style={{ width: '100%', height: 96, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }}
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
                <Typography sx={{ fontWeight: 600, color: '#f2ede6', fontSize: 14, fontFamily: '"Fraunces", serif' }}>
                  {infoWindowPlace.name}
                </Typography>
                {infoWindowPlace.cuisine && (
                  <Typography sx={{ color: '#b0a89e', fontSize: 11 }}>{infoWindowPlace.cuisine}</Typography>
                )}
                {infoWindowPlace.visited && infoWindowPlace.visitedRating && (
                  <Typography sx={{ color: '#ffca28', fontSize: 11, fontWeight: 700, mt: 0.5 }}>
                    {'★'.repeat(infoWindowPlace.visitedRating)}{'☆'.repeat(5 - infoWindowPlace.visitedRating)}
                  </Typography>
                )}
                {!infoWindowPlace.visited && (
                  <Typography sx={{ color: '#ff8a65', fontSize: 11, fontWeight: 700, mt: 0.5 }}>Want to try</Typography>
                )}
                {infoWindowPlace.instagram && (
                  <Box component="a" href={infoWindowPlace.instagram} target="_blank" rel="noopener"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, textDecoration: 'none', color: '#f48fb1', fontSize: 11, fontWeight: 600 }}>
                    📷 Instagram
                  </Box>
                )}
                <Box component="a" href={googleMapsUrl(infoWindowPlace)} target="_blank" rel="noopener"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75, color: '#8ab4f8', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                  <DirectionsIcon sx={{ fontSize: 13 }} /> Open in Google Maps
                </Box>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Near me — top-left so it doesn't fight the native zoom (top-right on
          desktop) or the bottom controls on mobile. */}
      {mapsLoaded && (
        <Tooltip title="Show my location">
          <IconButton
            onClick={handleNearMe}
            sx={{
              position: 'absolute', top: 12, left: 12, zIndex: 5,
              bgcolor: 'rgba(13,13,20,0.9)', border: '1px solid rgba(255,255,255,0.14)',
              color: userLoc ? '#4285f4' : 'text.secondary',
              '&:hover': { bgcolor: 'rgba(13,13,20,1)' },
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              width: 42, height: 42,
            }}
          >
            {locating ? <CircularProgress size={20} /> : <MyLocationIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}

      {/* Legend — slide up on mobile so it clears the toggle pill + FAB row,
          and reserve the Add FAB a column on the right. */}
      {mapsLoaded && legendItems.length > 0 && (
        <Box sx={{
          position: 'absolute',
          left: 8,
          bottom: isMobile ? 'calc(84px + env(safe-area-inset-bottom))' : 26,
          maxWidth: isMobile ? 'calc(100% - 90px)' : 'calc(100% - 100px)',
          display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
          bgcolor: 'rgba(13,13,20,0.8)', backdropFilter: 'blur(4px)',
          borderRadius: 1.5, px: 1.25, py: 0.5, border: '1px solid rgba(255,255,255,0.08)',
        }}>
          {legendItems.map(item => (
            <Box key={`${item.shape}-${item.label}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: item.shape === 'pin' ? 8 : 9,
                height: item.shape === 'pin' ? 8 : 9,
                bgcolor: item.color,
                borderRadius: item.shape === 'pin' ? '50% 50% 50% 0' : '50%',
                transform: item.shape === 'pin' ? 'rotate(-45deg)' : 'none',
                border: '1px solid rgba(13,13,20,0.6)',
              }} />
              <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {isMobile ? (
        <>
          {mobileView === 'list' ? (
            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>{sidebar}</Box>
          ) : (
            <>
              {/* Mobile map header: wordmark + inline search + filter toggle.
                  Having search here means users don't have to flip back to
                  list view to narrow pins. */}
              <AppBar position="static" elevation={0} sx={{
                bgcolor: 'background.default',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                zIndex: 10,
              }}>
                <Toolbar sx={{ minHeight: '56px !important', gap: 1, px: 1.5 }}>
                  <AppSwitcher>
                    <RestaurantIcon sx={{ color: 'primary.main', fontSize: 20, mr: 0.5 }} />
                    <Typography variant="h6" fontWeight={700}>FoodList</Typography>
                  </AppSwitcher>
                  {/* Search pill */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: 'rgba(244,239,233,0.06)',
                    border: '1px solid rgba(244,239,233,0.08)',
                    borderRadius: 99, px: 1.25, height: 36,
                    '&:focus-within': {
                      borderColor: 'rgba(227,105,80,0.5)',
                      bgcolor: 'rgba(244,239,233,0.08)',
                    },
                  }}>
                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <InputBase
                      placeholder="Search places"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      sx={{
                        flex: 1, fontSize: '0.86rem', color: 'text.primary',
                        '& input::placeholder': { color: 'text.disabled', opacity: 1 },
                      }}
                    />
                    {search && (
                      <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                        <ClearIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                      </IconButton>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setMobileFiltersOpen(v => !v)}
                    sx={{
                      color: (filterCategory || filterCuisine || mobileFiltersOpen) ? 'primary.main' : 'text.secondary',
                      bgcolor: mobileFiltersOpen ? 'rgba(227,105,80,0.1)' : 'transparent',
                      border: '1px solid',
                      borderColor: mobileFiltersOpen ? 'rgba(227,105,80,0.3)' : 'rgba(244,239,233,0.08)',
                      borderRadius: 99,
                      width: 36, height: 36,
                    }}
                  >
                    <TuneIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Toolbar>
              </AppBar>

              <Collapse in={mobileFiltersOpen}>
                {/* Tabs row — tap-friendly segmented control */}
                <Box sx={{
                  display: 'flex', gap: 0.75, px: 1.5, pt: 1, pb: 0.5,
                  overflowX: 'auto', scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}>
                  {[
                    { label: 'All', value: stats.total, tab: 'all' },
                    { label: 'To try', value: stats.want, tab: 'want' },
                    { label: 'Visited', value: stats.visited, tab: 'visited' },
                  ].map(({ label, value, tab }) => {
                    const active = filterTab === tab;
                    return (
                      <Chip
                        key={tab}
                        label={`${label} · ${value}`}
                        size="small"
                        onClick={() => setFilterTab(tab)}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                          height: 30, fontSize: '0.76rem', fontWeight: active ? 700 : 500,
                          borderRadius: 99,
                          bgcolor: active ? 'rgba(227,105,80,0.18)' : 'transparent',
                          color: active ? 'primary.light' : 'text.secondary',
                          borderColor: active ? 'rgba(227,105,80,0.42)' : 'rgba(244,239,233,0.12)',
                        }}
                      />
                    );
                  })}
                </Box>
                {(cuisinesInUse.length > 0 || categoriesInUse.length > 0) && (
                  <Box sx={{
                    display: 'flex', gap: 0.75, px: 1.5, py: 1,
                    overflowX: 'auto', scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}>
                    {categoriesInUse.map(({ value, label, color }) => {
                      const active = filterCategory === value;
                      return (
                        <Chip
                          key={value}
                          size="small"
                          label={
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                              {label}
                            </Box>
                          }
                          onClick={() => setFilterCategory(active ? '' : value)}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{
                            height: 28, fontSize: '0.74rem',
                            borderRadius: 99,
                            bgcolor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                            color: active ? 'text.primary' : 'text.secondary',
                            borderColor: active ? 'rgba(255,255,255,0.22)' : 'rgba(244,239,233,0.1)',
                          }}
                        />
                      );
                    })}
                    {cuisinesInUse.map(c => {
                      const active = filterCuisine === c;
                      return (
                        <Chip
                          key={c}
                          size="small"
                          label={`${c} ${cuisineCounts[c] || ''}`.trim()}
                          onClick={() => setFilterCuisine(active ? '' : c)}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{
                            height: 28, fontSize: '0.74rem',
                            borderRadius: 99,
                            bgcolor: active ? 'rgba(227,105,80,0.18)' : 'transparent',
                            color: active ? 'primary.light' : 'text.secondary',
                            borderColor: active ? 'rgba(227,105,80,0.42)' : 'rgba(244,239,233,0.1)',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Collapse>

              {layerBar}
              <Box sx={{ flex: 1, position: 'relative' }}>{mapView}</Box>

              {/* Add FAB — bottom-right, lifted above the toggle pill and safe-area */}
              <Fab
                color="primary" size="medium"
                sx={{
                  position: 'absolute',
                  bottom: 'calc(20px + env(safe-area-inset-bottom))',
                  right: 16,
                  zIndex: 15,
                  boxShadow: '0 10px 24px rgba(227,105,80,0.4)',
                }}
                onClick={() => { setEditingPlace(null); setFormOpen(true); }}
              >
                <AddIcon />
              </Fab>
            </>
          )}

          {/* Floating map/list toggle — hidden when the detail sheet is up so
              it doesn't peek through the drawer backdrop. Lifted above iOS
              safe-area so it doesn't sit on the home indicator. */}
          {!detailSheetPlace && (
            <Box
              onClick={() => setMobileView(v => (v === 'list' ? 'map' : 'list'))}
              sx={{
                position: 'absolute',
                bottom: 'calc(20px + env(safe-area-inset-bottom))',
                left: '50%', transform: 'translateX(-50%)',
                zIndex: 30, display: 'flex', alignItems: 'center', gap: 0.75,
                px: 2.5, py: 1.1, borderRadius: 99, cursor: 'pointer', userSelect: 'none',
                bgcolor: '#f2ede6', color: '#16121e',
                fontWeight: 800, fontSize: '0.8rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
                '&:active': { transform: 'translateX(-50%) scale(0.96)' },
                transition: 'transform .12s',
              }}
            >
              {mobileView === 'list'
                ? <><MapIcon sx={{ fontSize: 17 }} /> Map</>
                : <><ListIcon sx={{ fontSize: 17 }} /> List</>}
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <Box sx={{ width: 380, flexShrink: 0, position: 'relative', borderRight: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            {sidebar}
          </Box>
          {mapView}
        </Box>
      )}

      <PlaceFormDialog
        open={formOpen}
        place={editingPlace}
        prefill={sharePrefill}
        mapsLoaded={mapsLoaded}
        onClose={() => { setFormOpen(false); setEditingPlace(null); setSharePrefill(null); }}
        onSave={handleSave}
      />

      <VisitedDialog
        open={!!visitedDialogPlace}
        place={visitedDialogPlace}
        onClose={() => setVisitedDialogPlace(null)}
        onSave={handleVisitedSave}
      />

      <PlaceDetailSheet
        place={detailSheetPlace}
        open={!!detailSheetPlace}
        onClose={() => setDetailSheetPlace(null)}
        onEdit={handleDetailEdit}
        onDelete={handleDetailDelete}
        onToggleVisited={handleDetailToggleVisited}
        onAddToList={handleAddProgramPlaceToList}
        distanceKm={detailSheetPlace ? placeDistanceKm(userLoc, detailSheetPlace) : null}
      />

      <SharedListsDrawer
        open={listsDrawerOpen}
        onClose={() => setListsDrawerOpen(false)}
        user={user}
        personalPlaces={places}
        programPlaces={programPlaces}
      />

      <ApiUsagePanel open={usagePanelOpen} onClose={() => setUsagePanelOpen(false)} />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={snackbar?.undoPlace ? 6000 : 3000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnackbar(null); }}
        message={snackbar?.message}
        action={snackbar?.undoPlace && (
          <Button size="small" onClick={handleUndoDelete} sx={{ color: 'primary.light', fontWeight: 700 }}>
            Undo
          </Button>
        )}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function ProgramPlaceCard({ place, selected, onClick }) {
  const programs = place.programs || [];
  const primaryProg = programs[0];
  const meta = primaryProg ? PROGRAM_META[primaryProg] : null;

  return (
    <Box onClick={onClick} sx={{
      p: 1.5, borderRadius: 2, cursor: 'pointer',
      border: selected ? `1px solid ${meta?.color || 'rgba(255,255,255,0.3)'}` : '1px solid rgba(255,255,255,0.06)',
      bgcolor: selected ? (meta?.bg || 'rgba(255,255,255,0.05)') : 'rgba(255,255,255,0.01)',
      borderLeft: `3px solid ${meta?.color || 'rgba(255,255,255,0.2)'}`,
      '&:hover': { bgcolor: meta?.bg || 'rgba(255,255,255,0.03)', borderColor: meta?.color || 'rgba(255,255,255,0.2)' },
      transition: 'all 0.15s',
    }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Avatar src={place.photoURL || undefined} variant="rounded"
          sx={{ width: 48, height: 48, flexShrink: 0, bgcolor: meta?.bg || 'rgba(255,255,255,0.08)', fontSize: '1.2rem', borderRadius: 1.5 }}>
          {!place.photoURL && '🍽️'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: '0.9rem' }}>{place.name}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
            {programs.map(p => (
              <Chip key={p} label={PROGRAM_META[p]?.short || p} size="small"
                sx={{ height: 16, fontSize: '0.58rem', bgcolor: PROGRAM_META[p]?.bg, color: PROGRAM_META[p]?.color, fontWeight: 600 }} />
            ))}
            {place.cuisine && (
              <Chip label={place.cuisine} size="small"
                sx={{ height: 16, fontSize: '0.58rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary' }} />
            )}
          </Box>
          {place.address && (
            <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block', mt: 0.3 }}>{place.address}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
